import { useEffect, useState } from 'react'
import { X, Download, Share2, PlusSquare, Smartphone, Home, Check } from 'lucide-react'
import { analyticsService } from '@/services/analytics'

// Import IOSPWAPrompt for iOS
import IOSPWAPrompt from 'react-ios-pwa-prompt'
import { CoreController } from '@/services/coreController'

type Platform = 'ios' | 'android' | 'desktop' | 'unknown'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [showIosGuide, setShowIosGuide] = useState(false)
  const [showAndroidGuide, setShowAndroidGuide] = useState(false)
  const [platform, setPlatform] = useState<Platform>('unknown')
  const [dismissed, setDismissed] = useState(false)

  // Detect platform and check PWA status
  useEffect(() => {
    const userAgent = window.navigator.userAgent

    // Check if already installed (in standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true

    if (isStandalone) {
      console.debug('✅ [PWA] Already installed in standalone mode')
      return
    }

    // Check if user previously dismissed
    const wasDismissed = localStorage.getItem('pwa-prompt-dismissed')
    if (wasDismissed) {
      setDismissed(true)
      console.debug('🚫 [PWA] Prompt was previously dismissed')
      return
    }

    // Detect platform
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform('ios')
      console.debug('📱 [PWA] iOS platform detected')
    } else if (/Android/.test(userAgent)) {
      setPlatform('android')
      console.debug('🤖 [PWA] Android platform detected')
    } else {
      setPlatform('desktop')
      console.debug('💻 [PWA] Desktop platform detected')

      // Vérifier le statut PWA
      console.debug('🔍 [PWA] Checking PWA status...')

      // Vérifier si le manifest est chargé
      if ('onbeforeinstallprompt' in window) {
        console.debug('✅ [PWA] beforeinstallprompt is supported')
      } else {
        console.warn('❌ [PWA] beforeinstallprompt NOT supported by this browser')
      }

      // Vérifier le service worker
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then(registration => {
          if (registration) {
            console.debug('✅ [PWA] Service Worker is active:', registration)
          } else {
            console.warn('❌ [PWA] No Service Worker registration found')
          }
        })
      }

      // Pour desktop, essayer de montrer le prompt après un délai
      // même sans beforeinstallprompt (fallback pour tests)
      const timer = setTimeout(() => {
        if (!dismissed && !showPrompt) {
          console.debug('⚠️ [PWA] No beforeinstallprompt event received, showing manual install prompt')
          setShowPrompt(true)
          analyticsService.trackEvent('pwa_install_prompt_shown', {
            platform: 'desktop',
            method: 'manual-fallback'
          })
        }
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [])

  // Listen for beforeinstallprompt event (Android/Desktop only)
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Only handle for Android/Desktop
      if (platform === 'ios') {
        return
      }

      console.debug('🎯 [PWA] beforeinstallprompt event fired')
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)

      // Show prompt after a delay (for Android and Desktop)
      if (platform === 'android' || platform === 'desktop') {
        const timer = setTimeout(() => {
          if (!dismissed) {
            console.debug(`✅ [PWA] Showing ${platform} installation prompt`)
            setShowPrompt(true)
            analyticsService.trackEvent('pwa_install_prompt_shown', {
              platform: platform
            })
          }
        }, 3000)

        return () => clearTimeout(timer)
      }
    }

    const handleAppInstalled = () => {
      console.debug('✅ [PWA] App installed successfully')
      setShowPrompt(false)
      setDeferredPrompt(null)
      analyticsService.trackEvent('pwa_installed', {
        platform: platform
      })
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [dismissed, platform])

  const handleInstallClick = async () => {
    if (platform === 'ios') {
      setShowIosGuide(true)
      analyticsService.trackEvent('pwa_ios_guide_opened')
      return
    }

    if (platform === 'android' && deferredPrompt) {
      analyticsService.trackEvent('pwa_install_clicked', { platform })
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === 'accepted') {
        setShowPrompt(false)
        analyticsService.trackEvent('pwa_install_accepted', { platform })
      } else {
        analyticsService.trackEvent('pwa_install_dismissed', { platform })
      }
      setDeferredPrompt(null)
      return
    }

    if (platform === 'android' && !deferredPrompt) {
      setShowAndroidGuide(true)
      analyticsService.trackEvent('pwa_android_guide_opened')
      return
    }

    if (!deferredPrompt) {
      // Fallback: instructions manuelles pour desktop
      console.debug('📥 [PWA] No native prompt, showing manual instructions')
      analyticsService.trackEvent('pwa_install_manual_instructions', { platform })

      // Afficher des instructions dans une alerte ou toast
      alert(`Pour installer l'application sur desktop :\n\n1. Cliquez sur le menu (⋮) dans la barre d'adresse\n2. Sélectionnez "Installer ${CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"}" ou "Install application"\n\nOu utilisez Chrome/Edge sur un site en HTTPS.`)
      return
    }

    console.debug('📥 [PWA] User clicked install button')
    analyticsService.trackEvent('pwa_install_clicked', { platform })

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      console.debug('✅ [PWA] User accepted installation')
      setShowPrompt(false)
      analyticsService.trackEvent('pwa_install_accepted', { platform })
    } else {
      console.debug('❌ [PWA] User dismissed installation')
      analyticsService.trackEvent('pwa_install_dismissed', { platform })
    }

    setDeferredPrompt(null)
  }

  const handleDismiss = () => {
    console.debug('🚫 [PWA] Prompt dismissed by user')
    setShowPrompt(false)
    setShowIosGuide(false)
    setShowAndroidGuide(false)
    setDismissed(true)
    localStorage.setItem('pwa-prompt-dismissed', 'true')
    analyticsService.trackEvent('pwa_prompt_dismissed', { platform })
  }

  const handleDontShowAgain = () => {
    handleDismiss()
  }

  const appName = CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"

  // Icon helpers for iOS guide
  const SafariIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </svg>
  )
  const ShareIcon = Share2
  const AddHomeScreenIcon = PlusSquare
  const DoneIcon = Check

  // Don't render anything if already dismissed or installed
  if (dismissed) {
    return null
  }

  // iOS illustrated guide modal
  if (showIosGuide) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
        <div className="bg-white w-full sm:w-[420px] sm:rounded-3xl rounded-t-3xl shadow-2xl p-6 animate-in slide-in-from-bottom-8 duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg text-gray-900">Installer sur iPhone</h3>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-5">
            Ajoutez {appName} à votre écran d'accueil pour un accès rapide.
          </p>

          <div className="space-y-4">
            {[
              {
                icon: SafariIcon,
                step: '1',
                title: 'Ouvrez Safari',
                desc: 'Utilisez le navigateur Safari sur votre iPhone.'
              },
              {
                icon: ShareIcon,
                step: '2',
                title: 'Appuyez sur Partager',
                desc: "Touchez l'icône Partage  au centre/bas de l'écran.",
              },
              {
                icon: AddHomeScreenIcon,
                step: '3',
                title: "Sur l'écran d'accueil",
                desc: "Faites défiler et touchez 'Sur l'écran d'accueil'.",
              },
              {
                icon: DoneIcon,
                step: '4',
                title: 'Ajouter',
                desc: "Confirmez en touchant 'Ajouter' en haut à droite.",
              }
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4 bg-gray-50 rounded-2xl p-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <item.icon className="w-4 h-4 text-gray-500" />
                    <h4 className="font-semibold text-gray-900">{item.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600 leading-snug">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={handleDismiss}
            className="w-full mt-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
          >
            J'ai compris
          </button>
        </div>
      </div>
    )
  }

  // Android illustrated guide modal
  if (showAndroidGuide) {
    const ChromeMenuIcon = () => (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 16a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-6a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0-6a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
      </svg>
    )
    const AddToHomeScreenIcon = PlusSquare
    const CheckIcon = Check

    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
        <div className="bg-white w-full sm:w-[420px] sm:rounded-3xl rounded-t-3xl shadow-2xl p-6 animate-in slide-in-from-bottom-8 duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-lg text-gray-900">Installer sur Android</h3>
            <button
              onClick={handleDismiss}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-5">
            Ajoutez {appName} à votre écran d'accueil pour un accès rapide.
          </p>

          <div className="space-y-4">
            {[
              {
                icon: Smartphone,
                step: '1',
                title: 'Ouvrez Chrome',
                desc: 'Utilisez le navigateur Chrome sur votre Android.'
              },
              {
                icon: ChromeMenuIcon,
                step: '2',
                title: 'Menu (⋮)',
                desc: "Appuyez sur les trois points en haut à droite de l'écran.",
              },
              {
                icon: AddToHomeScreenIcon,
                step: '3',
                title: "Ajouter à l'écran d'accueil",
                desc: "Faites défiler et touchez 'Ajouter à l'écran d'accueil'.",
              },
              {
                icon: CheckIcon,
                step: '4',
                title: 'Ajouter',
                desc: "Confirmez en touchant 'Ajouter' dans la fenêtre.",
              }
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-4 bg-gray-50 rounded-2xl p-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-bold">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <item.icon className="w-4 h-4 text-gray-500" />
                    <h4 className="font-semibold text-gray-900">{item.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600 leading-snug">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {deferredPrompt && (
            <button
              onClick={async () => {
                analyticsService.trackEvent('pwa_install_clicked', { platform })
                deferredPrompt.prompt()
                const { outcome } = await deferredPrompt.userChoice
                if (outcome === 'accepted') {
                  setShowAndroidGuide(false)
                  analyticsService.trackEvent('pwa_install_accepted', { platform })
                } else {
                  analyticsService.trackEvent('pwa_install_dismissed', { platform })
                }
                setDeferredPrompt(null)
              }}
              className="w-full mt-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
            >
              Installer maintenant
            </button>
          )}

          <button
            onClick={handleDismiss}
            className="w-full mt-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
          >
            J'ai compris
          </button>
        </div>
      </div>
    )
  }

  // iOS: Use react-ios-pwa-prompt component as fallback
  if (platform === 'ios' && !showIosGuide) {
    return (
      <IOSPWAPrompt
        delay={5000}
        copyTitle="Ajouter à l'écran d'accueil"
        copyShareStep="1) Appuyez sur Partager"
        copyAddToHomeScreenStep="2) Appuyez sur 'Sur l'écran d'accueil'"
        copyDescription="3) Appuyez sur 'Ajouter'"
        onClose={() => {
          console.debug('🚫 [PWA] iOS prompt closed')
          handleDismiss()
          // Track installation on close since onInstall prop doesn't exist
          analyticsService.trackEvent('pwa_ios_installed', {})
        }}
      />
    )
  }

  // Android/Desktop/iOS: Custom installation prompt
  if (showPrompt && (platform === 'android' || platform === 'desktop' || platform === 'ios')) {
    const platformIcon = platform === 'ios' ? '🍎' : platform === 'android' ? '🤖' : '💻';
    const platformText = platform === 'ios' ? 'iPhone / iPad' : platform === 'android' ? 'Application Android' : 'Application Desktop';
    const installLabel = platform === 'ios' ? "Ajouter à l'écran d'accueil" : 'Installer';

    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[400px] z-50 animate-in slide-in-from-bottom-4 duration-300">
        <div className="bg-white rounded-3xl shadow-2xl p-5 border border-gray-100">
          <div className="flex items-start gap-4">
            {/* Logo */}
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg">
              <img src="/chamlelon_buiness.jpeg" alt={CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"} className="w-12 h-12" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Installer {CoreController.instance.appSettingResponse.config?.APP_NAME ?? "EasyService"}</h3>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <span>{platformIcon}</span>
                    {platformText}
                  </p>
                </div>
                <button
                  onClick={handleDismiss}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                  aria-label="Fermer"
                >
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Benefits */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3 mb-3">
                <p className="text-xs text-gray-700">
                  Accès instantané • ⚡ Performance optimale •
                </p>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Installez l'application pour un accès rapide depuis votre écran d'accueil
              </p>

              {/* Buttons */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleInstallClick}
                  className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-200"
                >
                  <Download className="w-4 h-4" />
                  {installLabel}
                </button>
                <button
                  onClick={() => {
                    if (platform === 'android') {
                      setShowAndroidGuide(true)
                      analyticsService.trackEvent('pwa_android_guide_opened')
                    } else if (platform === 'ios') {
                      setShowIosGuide(true)
                      analyticsService.trackEvent('pwa_ios_guide_opened')
                    } else {
                      // desktop: show generic manual instructions
                      alert(`Pour installer sur desktop :\n\n1. Cliquez sur le menu (⋮) dans la barre d'adresse\n2. Sélectionnez "Installer ${appName}" ou "Install application"\n\nOu utilisez Chrome/Edge sur un site en HTTPS.`)
                    }
                  }}
                  className="py-2 px-4 text-sm text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-medium"
                >
                  Voir la procédure
                </button>
                {platform === 'ios' && (
                  <button
                    onClick={() => {
                      setShowIosGuide(true)
                      analyticsService.trackEvent('pwa_ios_guide_opened')
                    }}
                    className="py-2 px-4 text-sm text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-medium"
                  >
                    Voir la procédure iPhone
                  </button>
                )}
                <button
                  onClick={handleDontShowAgain}
                  className="py-2 px-4 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors font-medium"
                >
                  Plus tard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return null
}
